import React from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';

import {
  MultiAutocompleteControl,
  useMultiAutocomplete,
  VirtualizedMenu,
  VirtualizedMenuItem,
} from '@adsk/alloy-react-dropdown';

import { FilterOption } from '../../types';

export interface VirtualFilterProps {
  options: FilterOption[];
  placeholder?: string;
  value?: unknown[];
  onChange?: (value: FilterOption[]) => void;
  isLoading?: boolean;
  isOpen?: boolean;
}

const DEFAULT_PROPS = {
  options: [],
  placeholder: 'Select',
  value: [],
  isLoading: false,
  isOpen: false,
};

const VirtualFilter = ({
  options = DEFAULT_PROPS.options,
  placeholder = DEFAULT_PROPS.placeholder,
  value = DEFAULT_PROPS.value,
  onChange,
  isLoading = DEFAULT_PROPS.isLoading,
  isOpen = DEFAULT_PROPS.isOpen,
}: VirtualFilterProps) => {
  const selectedOptions =
    value &&
    options.filter((option) => value.some((val) => option.value === val));

  const { controlProps, menuProps, virtualProps } = useMultiAutocomplete({
    initialIsOpen: isOpen,
    items: options,
    selectedItems: selectedOptions,
    onSelectedItemsChange: (changes) => {
      onChange?.(changes?.selectedItems || []);
    },
  });

  const { getTotalSize, getVirtualItems } = useVirtualizer(virtualProps);
  const { selectedItems, inputValue: _, ...restMenuProps } = menuProps;
  const selectedItemsSize = selectedItems?.length || 0;

  return (
    <>
      <MultiAutocompleteControl
        {...controlProps}
        isLoading={isLoading}
        placeholder={placeholder}
      />

      <VirtualizedMenu
        {...restMenuProps}
        setScrollElement={virtualProps.setScrollElement}
        totalSize={getTotalSize()}
        virtualItems={getVirtualItems()}
        renderMenuItem={(props) => (
          <VirtualizedMenuItem
            key={props.key}
            {...props}
            start={props.start ?? 0}
            height={props.height ?? 0}
          />
        )}
        renderFooter={selectedItemsSize ? undefined : () => null}
      />
    </>
  );
};

VirtualFilter.displayName = 'SubmittalsVirtualFilter';

export default VirtualFilter;
